import {
    type BodyScrollEndEvent,
    type BodyScrollEvent,
    type CellClickedEvent,
    type CellEditingStartedEvent,
    type CellEditingStoppedEvent,
    type CellFocusedEvent,
    type CellKeyDownEvent,
    type ColDef,
    type ColGroupDef,
    type ColTypeDef,
    type ColumnMovedEvent,
    type ColumnPinnedEvent,
    type ColumnResizedEvent,
    type ColumnVisibleEvent,
    type CsvExportParams,
    type DisplayedColumnsChangedEvent,
    type EditValidationCommitType,
    type ExcelExportParams,
    type ExcelStyle,
    type FilterChangedEvent,
    type FindChangedEvent,
    type FindOptions,
    type FirstDataRenderedEvent,
    type FullWidthCellKeyDownEvent,
    type GetRowIdFunc,
    type GridColumnsChangedEvent,
    type GridOptions,
    type GridReadyEvent,
    type GridSizeChangedEvent,
    type ICellRendererParams,
    type IsRowMaster,
    type RowClassParams,
    type RowClassRules,
    type RowClickedEvent,
    type RowDataUpdatedEvent,
    type RowDoubleClickedEvent,
    type RowDragEndEvent,
    type RowDragEnterEvent,
    type RowHeightParams,
    type RowSelectionOptions,
    type SelectionChangedEvent,
    type SideBarDef,
    type SizeColumnsToContentStrategy,
    type SizeColumnsToFitGridStrategy,
    type SizeColumnsToFitProvidedWidthStrategy,
    type SortChangedEvent,
    type Theme,
    themeQuartz,
    type ToolPanelVisibleChangedEvent,
} from "ag-grid-enterprise";
// eslint-disable-next-line no-restricted-imports
import { AgGridReact } from "ag-grid-react";
import {
    type ForwardedRef,
    forwardRef,
    type ReactElement,
    type ReactNode,
    useContext,
    useImperativeHandle,
    useMemo,
    useRef,
} from "react";

import { cn } from "@btnet/utilities-core";

import { BuilderInfoContext } from "helpers/globalContext/BuilderInfoContext";
import { isWebview } from "helpers/webview/utilities";

import { handleWebviewDownload } from "utilities/document/fileDownload.utils";

import "./AgGridReact.less";

import { useAgGridLicense } from "./AgGridReact.utility";

export interface IAgGridReactProps<TData = unknown> {
    /**
     * Allows for sorting on multiple columns when clicking the column headers.
     */
    alwaysMultiSort?: boolean;

    /**
     * Column definitions for the grid. Determines what columns to show and their behavior.
     */
    columnDefs: (ColDef<TData> | ColGroupDef<TData>)[];

    /**
     * Column types for the grid. Allows for defining custom column definitions based on column type.
     */
    columnTypes?: {
        [key: string]: ColTypeDef;
    };

    /**
     * Data for the grid rows.
     */
    data: TData[];

    /**
     * Unique identifier for the grid instance. Useful when multiple grids are present
     * and for using the test methods getGridApi and getGridElement.
     **/
    gridId?: string;

    /**
     * Default column definition for the grid. Applied to all columns unless overridden.
     */
    defaultColDef?: ColDef<TData>;

    /**
     * Layout of the grid. Determines how the grid is rendered.
     * Default is 'normal'.
     * See: https://www.ag-grid.com/react-data-grid/grid-size/#dom-layout
     */
    domLayout?: "autoHeight" | "normal" | "print";

    /**
     * Callback function to get the CSS class for a row.
     */
    getRowClass?: (params: RowClassParams<TData>) => string | string[] | undefined;

    /**
     * Map of CSS class name -> predicate (or boolean expression). Each row gets the classes
     * whose predicate returns true. Use for conditional row styling based on row data.
     * See: https://www.ag-grid.com/react-data-grid/row-styles/#row-class-rules
     */
    rowClassRules?: RowClassRules<TData>;

    /**
     * Callback version of property `rowHeight` to set height for each row individually. Function should return a positive number of pixels, or return `null`/`undefined` to use the default row height.
     */
    getRowHeight?: (params: RowHeightParams<TData>) => number | undefined | null;

    /**
     * When grouping rows, sets the number of levels to expand by default. -1 for expand everything.
     */
    groupDefaultExpanded?: number;

    /**
     * Callback function to handle cell single click events.
     */
    onCellClicked?: (event: CellClickedEvent<TData>) => void;

    /**
     * Callback function to handle row single click events.
     */
    onRowClicked?: (event: RowClickedEvent<TData>) => void;

    /**
     * Callback function to handle row double click events.
     */
    onRowDoubleClicked?: (event: RowDoubleClickedEvent<TData>) => void;

    /**
     * Callback function to handle the event when a cell receives focus.
     */
    onCellFocused?: (event: CellFocusedEvent<TData>) => void;

    /**
     * Callback function to handle the event when cell editing has started.
     * Useful for capturing the edited row while its node is still live — the node on the matching
     * stop event can be a discarded one.
     */
    onCellEditingStarted?: (event: CellEditingStartedEvent<TData>) => void;

    /**
     * Callback function to handle the event when cell editing has stopped.
     * Useful for committing edits when the user clicks away from a cell.
     */
    onCellEditingStopped?: (event: CellEditingStoppedEvent<TData>) => void;

    /**
     * Callback function to handle key presses on a cell, including while it is being edited.
     * Fires for every keydown, whether or not an edit was in flight.
     * See: https://www.ag-grid.com/react-data-grid/grid-events/#reference-keyboard-cellKeyDown
     */
    onCellKeyDown?: (event: CellKeyDownEvent<TData> | FullWidthCellKeyDownEvent<TData>) => void;

    /**
     * Callback function to handle cell selection change events.
     */
    onSelectionChanged?: (event: SelectionChangedEvent<TData>) => void;

    /**
     * Callback function to handle sorting changed events.
     */
    onSortChanged?: (event: SortChangedEvent<TData>) => void;

    /**
     * Set the height of the rows in pixels.
     */
    rowHeight?: number;

    /**
     * Row selection mode for the grid. Determines how rows can be selected.
     * Can be used to customize single row vs multi row selection, display of checkboxes, etc.
     */
    rowSelection?: RowSelectionOptions;

    /**
     * Prevents columns from being movable via drag and drop.
     */
    suppressMovableColumns?: boolean;

    /**
     * Strategy for automatically sizing columns.
     * See: https://www.ag-grid.com/javascript-data-grid/column-sizing/#auto-sizing-columns
     */
    autoSizeStrategy?:
        | SizeColumnsToFitGridStrategy
        | SizeColumnsToFitProvidedWidthStrategy
        | SizeColumnsToContentStrategy;

    /**
     * Prevents sorting on multiple columns when shift-clicking the column headers.
     */
    suppressMultiSort?: boolean;

    /**
     * Delay in milliseconds before showing tooltips.
     */
    tooltipShowDelay?: number;

    /**
     * Determines when to show tooltips.
     * Standard shows up on hover.
     * WhenTruncated shows up on hover only when the cell content is truncated.
     */
    tooltipShowMode?: GridOptions["tooltipShowMode"];

    /**
     * Delay in milliseconds before hiding tooltips.
     */
    tooltipHideDelay?: number;

    /**
     * Whether the tooltip should remain visible when the mouse is moved over it.
     */
    tooltipInteraction?: boolean;
    /**
     * Callback function to handle the event when the first data is rendered in the grid.
     */
    onFirstDataRendered?: (event: FirstDataRenderedEvent<TData>) => void;
    /**
     * Callback fired whenever the grid container resizes (e.g. browser window resize,
     * sidebar open/close). Useful for re-running sizeColumnsToFit so flex columns
     * track the available width dynamically.
     */
    onGridSizeChanged?: (event: GridSizeChangedEvent<TData>) => void;
    /**
     * Configuration for the sidebar in the grid. Allows for showing/hiding side panels like columns and filters.
     * See: https://www.ag-grid.com/react-data-grid/side-bar/#side-bar-configuration
     */
    sideBar?: SideBarDef; // Only allowing SideBarDef for the better typing here, also allows for more customization.
    /**
     * Configuration for where the grand total row will be displayed.
     * See: https://www.ag-grid.com/react-data-grid/footers/#grand-total-row
     */
    grandTotalRow?: GridOptions["grandTotalRow"];
    /**
     * Definition for the auto group column when using row grouping.
     * See: https://www.ag-grid.com/react-data-grid/grouping/#auto-group-column
     */
    autoGroupColumnDef?: ColDef<TData>;
    /**
     * Configures how group columns are displayed.
     * Use "custom" to suppress the auto-generated group column and define your own via columnDefs.
     * See: https://www.ag-grid.com/react-data-grid/grouping-display-types/
     */
    groupDisplayType?: "singleColumn" | "multipleColumns" | "groupRows" | "custom";
    /**
     * Prevents the cell from receiving focus when clicked.
     * Useful when using cell renderers with interactive elements like buttons or links.
     */
    suppressCellFocus?: boolean;
    /**
     * Parameters for exporting the grid data to Excel. Allows for customizing the export behavior.
     * See: https://www.ag-grid.com/javascript-data-grid/excel-export-api/
     */
    defaultExcelExportParams?: ExcelExportParams;
    /**
     * Styles for formatting cells in Excel exports.
     */
    excelStyles?: ExcelStyle[];
    /**
     * Parameters for exporting the grid data to CSV. Allows for customizing the export behavior.
     * See: https://www.ag-grid.com/javascript-data-grid/csv-export/
     */
    defaultCsvExportParams?: CsvExportParams;
    /**
     * Whether to show the context menu on right click.
     *  */
    showContextMenu?: boolean;
    /**
     * Enables selection of cells. Can be a boolean or an object for advanced configuration.
     * See: https://www.ag-grid.com/react-data-grid/cell-selection/
     */
    cellSelection?: GridOptions["cellSelection"];
    /**
     * Configuration for the status bar at the bottom of the grid.
     * See: https://www.ag-grid.com/react-data-grid/status-bar/
     */
    statusBar?: GridOptions["statusBar"];
    /**
     * Whether to copy the column headers to the clipboard when copying cell values.
     */
    copyHeadersToClipboard?: boolean;
    /**
     * Indicate if the grid is loading data. Can be used to show a loading overlay.
     */
    loading?: boolean;
    /**
     * Custom loading overlay component to show when the grid is loading data.
     */
    loadingOverlayComponent?: ReactNode;

    /**
     * Callback function to handle column resized events.
     */
    onColumnResized?: (event: ColumnResizedEvent<TData>) => void;
    /**
     * Callback function to handle column moved events.
     */
    onColumnMoved?: (event: ColumnMovedEvent<TData>) => void;
    /**
     * Required to enable filter handlers in custom filter components.
     *
     */
    enableFilterHandlers?: boolean;
    /**
     * Component to show when there are no rows to display in the grid.
     */
    noRowsOverlayComponent?: ReactNode;
    /**
     * Hide no rows overlay when true.
     */
    suppressNoRowsOverlay?: boolean;
    /**
     * Callback function to handle the filter change events.
     */
    onFilterChanged?: (event: FilterChangedEvent) => void;
    /**
     * Callback fired when a sidebar tool panel is shown or hidden (via the side buttons or the
     * API). Use to react to a specific panel opening/closing (e.g. injecting custom content into
     * the Filters tool panel when it becomes visible).
     * See: https://www.ag-grid.com/react-data-grid/grid-events/#reference-toolpanel-toolPanelVisibleChanged
     */
    onToolPanelVisibleChanged?: (event: ToolPanelVisibleChangedEvent) => void;
    /**
     * Callback function to handle the grid ready event.
     */

    onGridReady?: (event: GridReadyEvent) => void;
    /**
     *  Allows for text selection in cells. Useful for copying cell values. default is false.
     */
    cellTextSelection?: boolean;

    /**
     * When enabled, editing stops when the grid loses focus.
     * This ensures cell edits are committed when clicking outside the grid.
     */
    stopEditingWhenCellsLoseFocus?: GridOptions["stopEditingWhenCellsLoseFocus"];

    accentedSort?: boolean;
    /**
     * Enables master/detail functionality in the grid.
     */
    masterDetail?: boolean;
    /**
     * Renderer function for the detail rows in master/detail mode.
     */
    detailCellRenderer?: (params: ICellRendererParams<TData>) => ReactElement | null;
    /**
     * Enables auto height for detail rows in master/detail mode.
     */
    detailRowAutoHeight?: boolean;
    /**
     * Callback function to determine which rows should be treated as master rows.
     * Only rows that return true will have detail functionality.
     */
    isRowMaster?: IsRowMaster;
    /**
     * Callback function to handle column pinned events.
     * */
    onColumnPinned?: (event: ColumnPinnedEvent<TData>) => void;
    /**
     * Callback function to handle column visible events.
     * */
    onColumnVisible?: (event: ColumnVisibleEvent<TData>) => void;
    /**
     * Callback fired when the list of grid columns changes (e.g. columnDefs updated to
     * add/remove columns). Fires after AG Grid has rebuilt its columns.
     * */
    onGridColumnsChanged?: (event: GridColumnsChangedEvent<TData>) => void;
    /**
     * Callback fired when the displayed columns change (add/remove, reorder, pin, or
     * visibility). Fires after AG Grid has recomputed the displayed column set.
     * */
    onDisplayedColumnsChanged?: (event: DisplayedColumnsChangedEvent<TData>) => void;
    /**
     * Theme for the grid. Allows for customizing the appearance of the grid.
     */
    theme?: Theme;
    /**
     * Set to `true` to enable the Grid to work with Tree Data.
     * See https://www.ag-grid.com/javascript-data-grid/tree-data-data/ for ways to supply treeData.
     * @default false
     * @agModule `TreeDataModule`
     */
    treeData?: boolean;
    /**
     * The name of the field to use in a data item to find the parent node of a node when using treeData=true.
     * The tree will be constructed via relationships between nodes using this field.
     * getRowId callback need to be provided as well for this to work.
     * It supports accessing nested fields using the dot notation.
     * @agModule `TreeDataModule`
     */
    treeDataParentIdField?: string;
    /**
     * The name of the field in a data item that contains an array of children.
     * When set, AG Grid will use the nested children approach for tree data.
     * See https://www.ag-grid.com/react-data-grid/tree-data-nesting/
     * @agModule `TreeDataModule`
     */
    treeDataChildrenField?: string;
    /**
     * Provide a pure function that returns a string ID to uniquely identify a given row. This enables the grid to work optimally with data changes and updates.
     * @initial
     */
    getRowId?: GetRowIdFunc<TData>;
    /**
     * Loads Google Fonts when they are specified in the theme. This is really the only way to load Inter font via the theme instead of overriding in css classes. (It still is not quite Inter-bds)
     */
    loadThemeGoogleFonts?: boolean;
    /**
     * Suppresses the display of aggregation function names (e.g., 'sum', 'avg') in column headers.
     * When true, only the column header name is shown even when aggregation is enabled.
     * See: https://www.ag-grid.com/react-data-grid/aggregation/#suppress-aggregation-in-header
     */
    suppressAggFuncInHeader?: boolean;
    /**
     * When true, aggregation functions use all row data regardless of active filters.
     * Useful when totals should always reflect the full dataset even when the grid is filtered.
     * See: https://www.ag-grid.com/react-data-grid/aggregation-filtering/#suppress-filtered-only
     */
    suppressAggFilteredOnly?: boolean;
    /**
     * Indicates that this grid is rendered inside a modal.
     * When true, adds a class to document.body to ensure AG Grid popups have proper z-index.
     */
    inModal?: boolean;
    /**
     * Locale text for the grid. Allows for customizing the text displayed in the grid for different languages.
     */
    localeText?: {
        [key: string]: string;
    };
    /**
     * Custom aggregation functions for the grid. Allows for defining custom aggregation logic for columns.
     * See: https://www.ag-grid.com/react-data-grid/aggregation-custom-functions/
     */
    aggFuncs?: GridOptions["aggFuncs"];
    /**
     * When true, editing starts with a single click instead of a double click.
     * Useful for mobile/touch devices where double-tap is unreliable.
     */
    singleClickEdit?: boolean;
    /**
     * How the grid treats an edit that fails a cell editor's `getValidationErrors`. `"block"` holds
     * the cell in edit mode (flagged invalid, errors on hover); `"revert"` restores the prior value.
     * See: https://www.ag-grid.com/react-data-grid/cell-editing-validation/
     */
    invalidEditValueMode?: EditValidationCommitType;
    /**
     * Additional context to make available to all cell renderers and editors
     * via `params.context`. Merged with the grid's internal context (e.g. builderInfo).
     */
    context?: Record<string, unknown>;
    /**
     * Data to display in pinned bottom rows. Useful for showing totals or summary rows.
     * See: https://www.ag-grid.com/react-data-grid/row-pinning/
     */
    pinnedBottomRowData?: TData[];
    /**
     * Data to display in pinned top rows. Useful for a non-editable placeholder row (e.g. an
     * in-progress inline-create row pinned above the list).
     * See: https://www.ag-grid.com/react-data-grid/row-pinning/
     */
    pinnedTopRowData?: TData[];
    /**
     * For Client side row model only. Fires when client has update date by setting row data or via transaction.
     */
    onRowDataUpdated?: (event: RowDataUpdatedEvent<TData>) => void;
    /**
     * Fires on each scroll of the grid body.
     * See: https://www.ag-grid.com/react-data-grid/grid-events/#reference-miscellaneous-bodyScroll
     */
    onBodyScroll?: (event: BodyScrollEvent<TData>) => void;
    /**
     * Fires once the grid body stops scrolling. AG Grid debounces this internally, so it marks the
     * end of a user's scroll gesture.
     * See: https://www.ag-grid.com/react-data-grid/grid-events/#reference-miscellaneous-bodyScrollEnd
     */
    onBodyScrollEnd?: (event: BodyScrollEndEvent<TData>) => void;
    /**
     * Initial state to restore when grid is created. Can be used to set initial filter, sort, column state, etc.
     * See: https://www.ag-grid.com/react-data-grid/grid-state/
     */
    initialState?: GridOptions["initialState"];
    /**
     * Method for getting the tree structure for the data in the aggrid. Will be used if treeData is true
     * See: https://www.ag-grid.com/javascript-data-grid/tree-data-paths/
     */
    getDataPath?: GridOptions["getDataPath"];
    /**
     * Comparator for determining the order of group rows within their parent.
     * Used to maintain consistent group ordering when sorting is applied.
     * See: https://www.ag-grid.com/javascript-data-grid/grouping-sorting/#custom-group-ordering
     */
    initialGroupOrderComparator?: GridOptions["initialGroupOrderComparator"];
    /**
     * Method to call when a grouped row is opened or closed
     * See: https://www.ag-grid.com/javascript-data-grid/grid-events/#reference-rowGrouping-rowGroupOpened
     */
    onRowGroupOpened?: GridOptions["onRowGroupOpened"];
    /**
     * When true, grid will not scroll to top when new data is provided,
     * See: https://www.ag-grid.com/javascript-data-grid/grid-options/#reference-scrolling-suppressScrollOnNewData
     */
    suppressScrollOnNewData?: GridOptions["suppressScrollOnNewData"];
    /**
     * Callback to perform additional sorting after the grid has sorted rows.
     * Allows re-ordering of rows after every sort operation, maintaining custom group ordering.
     * See: https://www.ag-grid.com/javascript-data-grid/row-sorting/#post-sort
     */
    postSortRows?: GridOptions["postSortRows"];
    /**
     * Set to true so that the grid doesn't virtualise the columns.
     * For example, if you have 100 columns, but only 10 visible due to horizontal scrolling, all 100 will always be rendered.
     * Be careful when using this as it can cause performance issues with large datasets. When true, the grid will render all columns in the DOM instead of virtualizing them.
     * This can be useful in react tests or when getting autosizing to work on mobile.
     */
    suppressColumnVirtualisation?: boolean;
    /**
     * Debounce the grid's response to vertical scrolling so rows redraw after the scroll settles
     * rather than on every scroll event, smoothing fast scrolls at the cost of briefly showing
     * blank rows mid-scroll.
     * See: https://www.ag-grid.com/react-data-grid/grid-options/#reference-scrolling-debounceVerticalScrollbar
     * @default false
     */
    debounceVerticalScrollbar?: boolean;
    /**
     * Number of rows rendered outside the visible viewport, above and below. Raising it keeps the
     * row recycler ahead of fast scrolling at the cost of more rows in the DOM. Be conservative
     * when `suppressColumnVirtualisation` is on, since the buffer then multiplies against every
     * column.
     * See: https://www.ag-grid.com/react-data-grid/dom-virtualisation/#row-buffer
     * @default 10
     */
    rowBuffer?: number;
    /**
     * Default behavior when the user drags a column edge to resize it.
     * `"shift"` makes the adjacent column compensate (preserves total grid width); omitted preserves AG Grid's default of pushing all columns to the right.
     * See: https://www.ag-grid.com/react-data-grid/column-sizing/#shift-resize
     */
    colResizeDefault?: "shift";
    /**
     * When `true`, only child nodes that pass the filter are shown when using tree data.
     * Parent nodes are displayed as long as at least one descendant matches, but sibling children
     * that do not match the filter are hidden. When `false` (default), all children of a matching
     * parent are shown regardless of whether they individually pass the filter.
     * See: https://www.ag-grid.com/react-data-grid/tree-data-filtering/
     * @agModule `TreeDataModule`
     */
    excludeChildrenWhenTreeDataFiltering?: boolean;
    /**
     * Callback to determine whether a group row starts open or closed when data loads.
     * Return `true` to open the group, `false` to close it.
     * Runs once per group row on initial render; use in place of `groupDefaultExpanded` when
     * per-group control is needed.
     * See: https://www.ag-grid.com/javascript-data-grid/row-grouping-opening-groups/
     */
    isGroupOpenByDefault?: GridOptions["isGroupOpenByDefault"];

    /**
     * Configuration for the selection column when using checkboxLocation: "selectionColumn".
     * See: https://www.ag-grid.com/javascript-data-grid/row-selection-multi-row/#selection-column-def
     */
    selectionColumnDef?: GridOptions["selectionColumnDef"];
    /**
     * When scrolling through an expanded group, the group row will stick to the top of the grid. To prevent this behaviour, set the suppressGroupRowsSticky property to true.
     */
    suppressGroupRowsSticky?: boolean;
    /**
     * Text used to perform a quick filter across all columns. The grid keeps only the rows
     * whose combined cell text matches every whitespace-separated word (case-insensitive).
     * See: https://www.ag-grid.com/react-data-grid/filter-quick/
     */
    quickFilterText?: string;
    /**
     * Caches each row's quick filter text so repeated quick filtering avoids re-deriving it.
     * Recommended when the grid stays mounted and the user types into a quick filter box.
     * When enabled, call `api.resetQuickFilter()` after editing row data so the cache rebuilds.
     * See: https://www.ag-grid.com/react-data-grid/filter-quick/#caching-quick-filter
     * @default false
     */
    cacheQuickFilter?: boolean;
    /**
     * When true, the quick filter also searches the text of columns that are currently hidden.
     * See: https://www.ag-grid.com/react-data-grid/filter-quick/#hidden-columns
     */
    includeHiddenColumnsInQuickFilter?: boolean;
    /**
     * DOM element to use as the parent for grid popups (context menu, column menu, filters,
     * etc.), passed through to AG Grid's `popupParent`. Defaults to `document.body` when not
     * provided.
     * See: https://www.ag-grid.com/react-data-grid/grid-options/#reference-miscellaneous-popupParent
     */
    popupParent?: HTMLElement | null;
    /**
     * Text to search for using AG Grid's Find feature. Unlike `quickFilterText`, Find keeps all
     * rows visible and highlights / navigates matches instead of filtering them out. Drive it from
     * a search box and pair it with `onFindChanged` for an "N of M" counter.
     * See: https://www.ag-grid.com/react-data-grid/find/
     */
    findSearchValue?: string;
    /**
     * Options for the Find feature (case sensitivity, current-page-only, detail search).
     * See: https://www.ag-grid.com/react-data-grid/find/#find-options
     */
    findOptions?: FindOptions;
    /**
     * Callback fired when the Find search value or active match changes. Use to drive an
     * "N of M" match counter and to enable / disable next / previous controls.
     * See: https://www.ag-grid.com/react-data-grid/find/#find-events
     */
    onFindChanged?: (event: FindChangedEvent<TData>) => void;
    /**
     * Lets the grid perform the row move in its own state when a row is dragged, rather than
     * leaving the reordering to the application. Pair with `onRowDragEnd` to sync the result back.
     * See: https://www.ag-grid.com/react-data-grid/row-dragging/#managed-dragging
     */
    rowDragManaged?: boolean;
    /**
     * Keeps the dragged row in place until the drop, showing only the drop indicator while the row
     * is in flight, instead of live-moving it through the list under the cursor.
     * See: https://www.ag-grid.com/react-data-grid/row-dragging/#suppress-move-when-dragging
     */
    suppressMoveWhenRowDragging?: boolean;
    /**
     * Delay in ms before a hovered group expands, or before a dragged row tries to become a child
     * of the row under it. Raising it makes deep re-parenting less twitchy.
     */
    rowDragInsertDelay?: number;
    /**
     * Disables row dragging entirely (e.g. a read-only or locked grid) without removing the
     * drag configuration.
     */
    suppressRowDrag?: boolean;
    /**
     * Validates a candidate drop position while a row is being dragged. Return `false` to reject
     * the position, which withholds the drop indicator and snaps the row back on release.
     * The matching `onRowDragEnd` fires for rejected drops too - read `event.rowsDrop.allowed`
     * there rather than remembering this callback's last return value, which goes stale when the
     * grid rejects a drop without consulting the callback at all.
     * See: https://www.ag-grid.com/react-data-grid/row-dragging/#is-row-valid-drop-position
     */
    isRowValidDropPosition?: GridOptions<TData>["isRowValidDropPosition"];
    /**
     * Fires when a row drag finishes - including for drops the grid rejected, so guard on
     * `event.rowsDrop?.allowed` before persisting. With `rowDragManaged` the node tree already
     * reflects an allowed move by the time this fires, so the new order can be read back off
     * `event.api`. `suppressMoveWhenRowDragging` only suppresses the live move *during* the drag,
     * not the commit on drop.
     */
    onRowDragEnd?: (event: RowDragEndEvent<TData>) => void;
    /**
     * Fires when a dragged row first enters the grid. Useful for closing anything (an open cell
     * editor, a menu) that shouldn't survive the drag.
     */
    onRowDragEnter?: (event: RowDragEnterEvent<TData>) => void;
}

function AgGridInner<TData>(
    props: IAgGridReactProps<TData>,
    ref: ForwardedRef<Partial<AgGridReact<TData>>>,
) {
    const gridRef = useRef<AgGridReact<TData>>(null);
    const builderInfo = useContext(BuilderInfoContext);

    // Set the enterprise license before the grid renders. Replaces the former
    // eagerly-rendered <AGGridProvider> that pinned ag-grid into the entry bundle.
    useAgGridLicense();

    // Can add customization for the theme here if needed
    const theme = props.theme ?? themeQuartz;

    // Expose the internal grid API to the parent
    // Note: We return getters that access gridRef.current at call time to ensure
    // the API is available even if the ref wasn't set during the initial render.
    // The api getter returns a proxied object that overrides exportDataAsExcel
    // to handle webview downloads natively (blob URLs fail in mobile webviews).
    useImperativeHandle(ref, () => ({
        get api() {
            return gridRef.current?.api
                ? {
                      ...gridRef.current?.api,
                      exportDataAsExcel: (params?: ExcelExportParams) => {
                          if (isWebview()) {
                              const blob = gridRef.current?.api.getDataAsExcel(params) as
                                  | Blob
                                  | undefined;
                              if (blob) {
                                  const rawFileName = params?.fileName ?? "export";
                                  const filename =
                                      typeof rawFileName === "string" ? rawFileName : "export.xlsx";
                                  if (
                                      handleWebviewDownload(
                                          blob,
                                          filename.endsWith(".xlsx")
                                              ? filename
                                              : `${filename}.xlsx`,
                                      )
                                  ) {
                                      return;
                                  }
                              }
                          }
                          gridRef.current?.api.exportDataAsExcel(params);
                      },
                  }
                : undefined;
        },
        get columnApi() {
            return gridRef.current?.api;
        },
    }));

    // Provide builderInfo in context for value formatters and cell renderers
    const gridContext = useMemo(
        () => ({ ...props.context, builderInfo }),
        [builderInfo, props.context],
    );

    const defaultColDef: ColDef<TData> = useMemo(() => {
        return (
            props.defaultColDef ?? {
                // Default column behavior when no defaultColDef is provided
                sortable: false,
                editable: false,
                suppressMovable: false,
                wrapHeaderText: false,
                suppressHeaderMenuButton: false,
                suppressHeaderContextMenu: false,
                resizable: false,
            }
        );
    }, [props.defaultColDef]);

    const columnDefs = useMemo(() => {
        return props.columnDefs;
    }, [props.columnDefs]);

    const sideBar = useMemo(() => {
        return props.sideBar;
    }, [props.sideBar]);

    const autoGroupColumnDef = useMemo(() => {
        return props.autoGroupColumnDef;
    }, [props.autoGroupColumnDef]);

    const popupParent = useMemo(() => {
        return props.popupParent ?? document.body;
    }, [props.popupParent]);

    const excelStyles = useMemo(() => {
        return props.excelStyles;
    }, [props.excelStyles]);

    return (
        <AgGridReact
            aggFuncs={props.aggFuncs}
            gridId={props.gridId}
            domLayout={props.domLayout}
            className={cn("ag-grid-react", {
                "override-active-filter-button": props.defaultColDef?.suppressHeaderFilterButton,
                "ag-grid-in-modal": props.inModal,
            })}
            treeData={props.treeData}
            treeDataParentIdField={props.treeDataParentIdField}
            treeDataChildrenField={props.treeDataChildrenField}
            getRowId={props.getRowId}
            ref={gridRef}
            rowData={props.data}
            columnDefs={columnDefs}
            columnTypes={props.columnTypes}
            defaultColDef={defaultColDef}
            getRowClass={props.getRowClass}
            rowClassRules={props.rowClassRules}
            getRowHeight={props.getRowHeight}
            groupDefaultExpanded={props.groupDefaultExpanded}
            isGroupOpenByDefault={props.isGroupOpenByDefault}
            initialState={props.initialState}
            onCellClicked={props.onCellClicked}
            onRowClicked={props.onRowClicked}
            onRowDoubleClicked={props.onRowDoubleClicked}
            onCellFocused={props.onCellFocused}
            onCellEditingStarted={props.onCellEditingStarted}
            onCellEditingStopped={props.onCellEditingStopped}
            onCellKeyDown={props.onCellKeyDown}
            onSelectionChanged={props.onSelectionChanged}
            onSortChanged={props.onSortChanged}
            rowHeight={props.rowHeight}
            rowSelection={props.rowSelection}
            cellSelection={props.cellSelection}
            statusBar={props.statusBar}
            localeText={props.localeText}
            copyHeadersToClipboard={props.copyHeadersToClipboard}
            suppressCellFocus={props.suppressCellFocus}
            suppressMultiSort={props.suppressMultiSort}
            suppressDragLeaveHidesColumns={true}
            suppressAggFuncInHeader={props.suppressAggFuncInHeader}
            suppressAggFilteredOnly={props.suppressAggFilteredOnly}
            theme={theme}
            themeCssLayer="app"
            suppressMovableColumns={props.suppressMovableColumns}
            alwaysMultiSort={props.alwaysMultiSort}
            alwaysShowVerticalScroll={true}
            sideBar={sideBar}
            tooltipShowDelay={props.tooltipShowDelay}
            tooltipShowMode={props.tooltipShowMode}
            tooltipInteraction={props.tooltipInteraction}
            tooltipHideDelay={props.tooltipHideDelay}
            autoSizeStrategy={props.autoSizeStrategy}
            grandTotalRow={props.grandTotalRow}
            autoGroupColumnDef={autoGroupColumnDef}
            groupDisplayType={props.groupDisplayType}
            onFirstDataRendered={props.onFirstDataRendered}
            onGridSizeChanged={props.onGridSizeChanged}
            defaultExcelExportParams={props.defaultExcelExportParams}
            defaultCsvExportParams={props.defaultCsvExportParams}
            suppressContextMenu={!props.showContextMenu}
            excelStyles={excelStyles}
            popupParent={popupParent}
            loading={props.loading}
            loadingOverlayComponent={props.loadingOverlayComponent}
            onColumnResized={props.onColumnResized}
            onColumnMoved={props.onColumnMoved}
            loadThemeGoogleFonts={props.loadThemeGoogleFonts}
            enableFilterHandlers={props.enableFilterHandlers ?? true} // Default to true to enable filter handlers in custom filter components
            onFilterChanged={props.onFilterChanged}
            onToolPanelVisibleChanged={props.onToolPanelVisibleChanged}
            noRowsOverlayComponent={props.noRowsOverlayComponent}
            suppressNoRowsOverlay={props.suppressNoRowsOverlay}
            onGridReady={props.onGridReady}
            enableCellTextSelection={props.cellTextSelection}
            stopEditingWhenCellsLoseFocus={props.stopEditingWhenCellsLoseFocus}
            accentedSort={props.accentedSort}
            masterDetail={props.masterDetail}
            detailCellRenderer={props.detailCellRenderer}
            detailRowAutoHeight={props.detailRowAutoHeight}
            isRowMaster={props.isRowMaster}
            onColumnPinned={props.onColumnPinned}
            onColumnVisible={props.onColumnVisible}
            onGridColumnsChanged={props.onGridColumnsChanged}
            onDisplayedColumnsChanged={props.onDisplayedColumnsChanged}
            context={gridContext}
            onRowDataUpdated={props.onRowDataUpdated}
            onBodyScroll={props.onBodyScroll}
            onBodyScrollEnd={props.onBodyScrollEnd}
            getDataPath={props.getDataPath}
            initialGroupOrderComparator={props.initialGroupOrderComparator}
            onRowGroupOpened={props.onRowGroupOpened}
            suppressScrollOnNewData={props.suppressScrollOnNewData}
            suppressColumnVirtualisation={props.suppressColumnVirtualisation}
            debounceVerticalScrollbar={props.debounceVerticalScrollbar}
            rowBuffer={props.rowBuffer}
            singleClickEdit={props.singleClickEdit}
            invalidEditValueMode={props.invalidEditValueMode}
            postSortRows={props.postSortRows}
            excludeChildrenWhenTreeDataFiltering={props.excludeChildrenWhenTreeDataFiltering}
            selectionColumnDef={props.selectionColumnDef}
            pinnedBottomRowData={props.pinnedBottomRowData}
            pinnedTopRowData={props.pinnedTopRowData}
            suppressGroupRowsSticky={props.suppressGroupRowsSticky}
            colResizeDefault={props.colResizeDefault}
            quickFilterText={props.quickFilterText}
            cacheQuickFilter={props.cacheQuickFilter}
            includeHiddenColumnsInQuickFilter={props.includeHiddenColumnsInQuickFilter}
            findSearchValue={props.findSearchValue}
            findOptions={props.findOptions}
            onFindChanged={props.onFindChanged}
            rowDragManaged={props.rowDragManaged}
            suppressMoveWhenRowDragging={props.suppressMoveWhenRowDragging}
            rowDragInsertDelay={props.rowDragInsertDelay}
            suppressRowDrag={props.suppressRowDrag}
            isRowValidDropPosition={props.isRowValidDropPosition}
            onRowDragEnd={props.onRowDragEnd}
            onRowDragEnter={props.onRowDragEnter}
        />
    );
}

// `forwardRef` erases the generic type parameter, so we cast the wrapped component back to a
// generic-preserving signature. This lets callers write `<AgGridReact<IMyRow> ... />` and get
// TData inferred through columnDefs, data, event handlers, and the exposed grid ref.
const AgGrid = forwardRef(AgGridInner) as <TData = unknown>(
    props: IAgGridReactProps<TData> & { ref?: ForwardedRef<Partial<AgGridReact<TData>>> },
) => ReactElement;

export { AgGrid as AgGridReact };
export type { AgGridReact as AgGridReactType };
